import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, ShoppingBag, Percent, PoundSterling } from 'lucide-react';
import { api } from '../../lib/api';

const PLATFORM_COLOURS: Record<string, string> = {
  ebay: '#e53935', vinted: '#09b864', depop: '#ff2300', etsy: '#f56040',
  facebook: '#1877f2', grailed: '#1a1a1a', mercari: '#ff5722',
  poshmark: '#7b1fa2', shopify: '#95bf47', whatnot: '#ffc940',
};

interface AnalyticsData {
  period: { days: number };
  listings: { total: number; byStatus: Record<string, number> };
  revenue: { total: number; net: number; fees: number; profit: number | null };
  sales: { total: number; avgPrice: number };
  platforms: { platform: string; revenue: number; sales: number }[];
  dailySeries: { date: string; revenue: number }[];
}

export function AnalyticsPage() {
  const [days, setDays] = useState(30);

  const { data, isLoading } = useQuery<AnalyticsData>({
    queryKey: ['analytics', days],
    queryFn: () => api.get('/analytics', { params: { days } }).then((r) => r.data),
  });

  const maxDaily = data ? Math.max(...data.dailySeries.map((d) => d.revenue), 1) : 1;
  const totalPlatformRevenue = data ? data.platforms.reduce((s, p) => s + p.revenue, 0) : 0;

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-base font-semibold tracking-tight">Analytics</h1>
          <p className="text-[11.5px] text-white/25 mt-0.5">
            {isLoading ? 'Loading...' : `Last ${days} days · Across all platforms`}
          </p>
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-md text-[11px] border transition-colors ${
                days === d
                  ? 'border-primary-500/50 text-primary-300 bg-primary-500/10'
                  : 'border-white/[0.08] text-white/35 bg-white/[0.03]'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5">
        <KPI
          icon={PoundSterling}
          label="Net revenue"
          value={data ? `£${(data.revenue.net / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
          sub={data ? `£${(data.revenue.fees / 100).toFixed(2)} in fees` : ''}
          colour="#2de8c8"
        />
        <KPI
          icon={TrendingUp}
          label="Net profit"
          value={data?.revenue.profit != null ? `£${(data.revenue.profit / 100).toFixed(2)}` : '—'}
          sub={data?.revenue.profit != null ? 'after buy costs' : 'add buy prices to track'}
          colour={data?.revenue.profit != null && data.revenue.profit >= 0 ? '#4ade80' : '#f97316'}
        />
        <KPI
          icon={ShoppingBag}
          label="Items sold"
          value={data ? String(data.sales.total) : '—'}
          sub={data && data.sales.total > 0 ? `avg £${(data.sales.avgPrice / 100).toFixed(2)}` : 'no sales yet'}
          colour="#7c5cfc"
        />
        <KPI
          icon={TrendingUp}
          label="Active listings"
          value={data ? String(data.listings.byStatus['live'] ?? 0) : '—'}
          sub={data ? `${data.listings.total} total` : ''}
          colour="#a78bfa"
        />
        <KPI
          icon={Percent}
          label="Draft listings"
          value={data ? String(data.listings.byStatus['draft'] ?? 0) : '—'}
          sub="ready to crosslist"
          colour="#4ade80"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        {/* Revenue by platform */}
        <div className="card p-5">
          <h2 className="text-[13px] font-semibold mb-0.5">Revenue by platform</h2>
          <p className="text-[11px] text-white/30 mb-4">
            {totalPlatformRevenue > 0
              ? `£${(totalPlatformRevenue / 100).toLocaleString('en-GB', { minimumFractionDigits: 2 })} total`
              : 'No sales in this period'}
          </p>
          {data && data.platforms.length > 0 ? (
            <div className="space-y-3">
              {data.platforms
                .sort((a, b) => b.revenue - a.revenue)
                .map((p) => {
                  const pct = totalPlatformRevenue > 0 ? (p.revenue / totalPlatformRevenue) * 100 : 0;
                  return (
                    <div key={p.platform}>
                      <div className="flex items-center justify-between text-[11px] mb-1">
                        <span className="capitalize text-white/70">{p.platform}</span>
                        <span className="text-white/85 font-medium">
                          £{(p.revenue / 100).toFixed(2)}
                          <span className="text-white/30 ml-1">· {p.sales} sale{p.sales !== 1 ? 's' : ''}</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${pct}%`, background: PLATFORM_COLOURS[p.platform] ?? '#666' }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="text-[11px] text-white/30 py-4">No sales data yet for this period</div>
          )}
        </div>

        {/* Daily revenue sparkline */}
        <div className="card p-5">
          <h2 className="text-[13px] font-semibold mb-0.5">Daily revenue</h2>
          <p className="text-[11px] text-white/30 mb-4">Last {days} days</p>
          <div className="h-32 flex items-end gap-0.5">
            {data?.dailySeries.map((d) => {
              const h = maxDaily > 0 ? Math.max((d.revenue / maxDaily) * 100, d.revenue > 0 ? 8 : 2) : 2;
              return (
                <div
                  key={d.date}
                  title={`${d.date}: £${(d.revenue / 100).toFixed(2)}`}
                  className={`flex-1 rounded-t-sm transition-all duration-300 ${d.revenue > 0 ? 'bg-secondary-400/60 hover:bg-secondary-400' : 'bg-white/[0.04]'}`}
                  style={{ height: `${h}%` }}
                />
              );
            })}
          </div>
          {data && data.dailySeries.every((d) => d.revenue === 0) && (
            <p className="text-[10px] text-white/25 mt-2 text-center">Sales will appear as bars once you record them</p>
          )}
        </div>
      </div>

      {/* Listing status breakdown */}
      {data && data.listings.total > 0 && (
        <div className="card p-5">
          <h2 className="text-[13px] font-semibold mb-4">Inventory breakdown</h2>
          <div className="flex gap-6">
            {Object.entries(data.listings.byStatus).map(([status, count]) => {
              const colour = { live: '#2de8c8', draft: '#a78bfa', sold: '#4ade80', failed: '#f97316', delisted: '#666', publishing: '#7c5cfc' }[status] ?? '#666';
              return (
                <div key={status} className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: colour }} />
                  <span className="text-[11px] text-white/50 capitalize">{status}</span>
                  <span className="text-[11px] font-semibold" style={{ color: colour }}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, colour }: {
  icon: React.ComponentType<{ size?: number | string; style?: React.CSSProperties }>;
  label: string; value: string; sub: string; colour: string;
}) {
  return (
    <div className="card p-3.5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: colour }} />
      <div className="flex items-center gap-2 mb-1">
        <Icon size={12} style={{ color: colour }} />
        <div className="text-[10.5px] text-white/30">{label}</div>
      </div>
      <div className="text-[21px] font-semibold tracking-tight" style={{ color: colour }}>{value}</div>
      <div className="text-[10px] mt-0.5 text-white/30">{sub}</div>
    </div>
  );
}
